import React, { useState } from 'react'
import {
  makeStyles,
  Typography,
  Select,
  MenuItem,
  FormControl,
  MenuProps,
  Link,
} from '@material-ui/core'
import ArrowDropDownRoundedIcon from '@material-ui/icons/ArrowDropDownRounded'
import Image from 'next/image'
import { deepPurple } from '@material-ui/core/colors'
import { useRouter } from 'next/router'
import { urls } from '../../utils/config'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 30px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  linkContainer: {
    display: 'inline-flex',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      margin: '1rem 0',
    },
    '& > a': {
      fontSize: '.84rem',
      color: 'rgba(43, 24, 65, 1)',
      padding: '.4rem 1rem',
      cursor: 'pointer',
      borderRadius: '.8rem',
      margin: '0 .4rem',
      fontWeight: '600',
    },
    '& > a.active': {
      background: 'rgba(0, 0, 0, 0.04)',
      color: 'black',
    },
  },
  selectContainer: {
    minWidth: '12rem',
  },
  filterContainer: {
    margin: '1rem 0',
  },
  price: {},
  select: {
    width: '100%',
    background: 'white',
    color: deepPurple[500],
    fontWeight: 600,
    borderStyle: 'none',
    borderWidth: 2,
    borderRadius: 12,
    padding: '.4rem .8rem',
    fontSize: '1rem',
    minWidth: '7rem',
    '&:focus': {
      borderRadius: 12,
      background: 'white',
      borderColor: deepPurple[100],
    },
  },
  paper: {
    borderRadius: 12,
    marginTop: 8,
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
    background: 'white',
    fontSize: '1rem',
    '& li': {
      fontWeight: 200,
      paddingTop: 12,
      paddingBottom: 12,
      fontSize: '.8rem',
    },
    '& li:hover': {
      background: deepPurple[100],
    },
    '& li.Mui-selected': {
      color: 'white',
      background: deepPurple[400],
      fontWeight: 600,
    },
    '& li.Mui-selected:hover': {
      background: deepPurple[500],
    },
  },
  valueContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    marginRight: '.2rem',
  },
  valueImageContainer: {
    height: '1.2rem',
    width: '1rem',
    display: 'inline-block',
    position: 'relative',
    verticalAlign: 'middle',
    overflow: 'hidden',
    marginRight: '.8rem',
  },
  value: {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    color: 'black',
    textTransform: 'capitalize',
  },
}))

interface navLink {
  name: string
  url: string
}
interface navLinks {
  [key: string]: navLink[]
}

const navLinks: navLinks = {
  exchanges: [
    { name: 'Overview', url: urls.productExchanges },
    { name: 'Cryptocurrency exchanges', url: urls.exchanges },
    { name: 'Peer to peer exchanges', url: urls.p2p },
    { name: 'Decentralized exchanges', url: urls.decentralizedExchanges },
  ],
  wallets: [
    { name: 'Overview', url: urls.productWallets },
    { name: 'Custodial wallets', url: urls.custodialWallets },
    { name: 'Non Custodial wallets', url: urls.nonCustodialWallets },
    { name: 'Hardware wallets', url: urls.hardwareWallets },
  ],
  earn: [
    { name: 'Overview', url: urls.productEarn },
    { name: 'Savings accounts', url: urls.savingAccounts },
    { name: 'DeFi lending rates', url: urls.defi },
    { name: 'ETH staking', url: urls.ethereumStaking },
  ],
  rewards: [
    { name: 'Overview', url: urls.productRewards },
    { name: 'Cards', url: urls.cryptoCards },
    { name: 'Deals', url: urls.deals },
  ],
}

interface ProductSelectOption {
  name: string
  value: string
  icon: string
}

const selectOptions = [
  { value: 'exchanges', name: 'Exchanges', icon: '/svg/product/exchanges-icon.svg' },
  { value: 'wallets', name: 'Wallets', icon: '/svg/product/wallets-icon.svg' },
  { value: 'earn', name: 'Earn', icon: '/svg/product/earn-icon.svg' },
  { value: 'rewards', name: 'Rewards', icon: '/svg/product/rewards-icon.svg' },
] as ProductSelectOption[]

export default function Nav(): JSX.Element {
  const classes = useStyles({})
  const selectProps = {
    classes: {
      paper: classes.paper,
      list: classes.list,
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
  } as MenuProps
  const router = useRouter()
  const defaultOption = selectOptions.find(
    (option) => option.value === router.pathname.split('/')[2],
  )
  const [currentNav] = useState(defaultOption)
  const handleSelect = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const value = event.target.value
    switch (value) {
      case 'exchanges':
        router.push(urls.productExchanges)
        break
      case 'wallets':
        router.push(urls.productWallets)
        break
      case 'earn':
        router.push(urls.productEarn)
        break
      case 'rewards':
        router.push(urls.productRewards)
        break
      default:
        break
    }
  }

  return currentNav ? (
    <div className={classes.root}>
      <div className={classes.selectContainer}>
        <FormControl>
          <Select
            id="select-product-page"
            value={currentNav.value}
            title={currentNav.name}
            onChange={handleSelect}
            classes={{ root: classes.select }}
            MenuProps={selectProps}
            IconComponent={ArrowDropDownRoundedIcon}
            disableUnderline
            renderValue={() => (
              <span className={classes.valueContainer}>
                <span className={classes.valueImageContainer}>
                  <Image src={currentNav.icon} layout="fill" objectFit="contain" />
                </span>
                <Typography variant="body2" className={classes.value}>
                  {currentNav.name}
                </Typography>
              </span>
            )}
          >
            {selectOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className={classes.linkContainer}>
        {navLinks[currentNav.value as string]?.map((link, index) => (
          <Link
            href={link.url}
            key={index}
            underline="none"
            className={router.pathname === link.url ? 'active' : ''}
            rel="prefetch"
            component="a"
          >
            {link.name}
          </Link>
        ))}
      </div>
    </div>
  ) : (
    <> </>
  )
}
